<template>
  <div class="mb-3">
    <label :for="'inputField-' + index" class="form-label">{{ label }}</label>

    <p v-if="inputType === 'usersignature'" class="fst-italic">
      Diese Variable wird beim Erstellen eines Dokuments automatisch mit dem Namen des angemeldeten Benutzers überschrieben, wenn dieser die entsprechende
      Berechtigung hat.
    </p>

    <input
      type="text"
      class="form-control"
      :id="'inputField-' + index"
      v-model="inputvalue"
      @input="validateInput"
      @change="inputChanged"
      :class="{ 'is-invalid': !isValid && inputvalue.length > 0, 'is-valid': isValid && inputvalue.length > 0 }"
    />
    <div v-if="!isValid && inputvalue.length > 0" class="invalid-feedback">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, PropType, watch } from 'vue';

  const props = defineProps({
    index: {
      type: Number as PropType<number>,
      default: 0,
    },
    modelValue: {
      type: String as PropType<string>,
      required: true,
    },
    label: {
      type: String as PropType<string>,
      default: 'Text',
    },
    min: {
      type: Number as PropType<number>,
      default: 0,
    },
    max: {
      type: Number as PropType<number>,
      default: 40,
    },
    inputType: {
      type: String as PropType<string | null>,
      default: 'text', //text, int, float, currency
    },
  });

  const emit = defineEmits(['update:modelValue']);

  // Reactive properties
  const inputvalue = ref(props.modelValue);

  watch(
    () => props.modelValue,
    (neuerValue) => {
      if (neuerValue === null || neuerValue === undefined) {
        neuerValue = '';
        // Optional: direkt validieren, falls du sofort die Fehlermeldung aktualisieren willst:
        validateInput();
      } else if (neuerValue != inputvalue.value) {
        // Wenn der neue Wert nicht dem aktuellen Wert entspricht, aktualisiere den inputvalue
        inputvalue.value = neuerValue;
        // Optional: direkt validieren, falls du sofort die Fehlermeldung aktualisieren willst:
        validateInput();
      }
    }
  );

  const validationErrorMsg = ref('');
  const regexPattern = computed<string>(() => {
    const min = props.min ?? 0;
    const max = props.max ?? Infinity;

    switch (props.inputType) {
      case 'text':
        validationErrorMsg.value = `Die Eingabe muss zwischen ${min} und ${max} Zeichen lang sein.`;
        // Regex for any character (including numbers and special characters) with min/max length
        return `^.{${min},${max}}$`;
      case 'int':
        validationErrorMsg.value = `Es dürfen nur Ganzzahlen eingegeben werden.`;
        // Regex for integer with min/max length
        return `^[-+]?\\d+$`;
      case 'float':
        validationErrorMsg.value = `Es dürfen nur Ganz- oder Kommazahlen eingegeben werden (Dezimaltrennzeichen: . oder ,).`;
        // Regex for float number with min/max length before and after decimal
        return `^[-+]?\\d*[.,]?\\d+$`;
      case 'currency':
        validationErrorMsg.value = `Die Eingabe muss als Währung formatiert sein (bspw. 3000 oder 3.000,00).`;
        // Regex for currency format with optional currency symbol, with min/max length before and after decimal
        return `^([$€£] ?)?\\d{1,3}([.]?\\d{3})*([.,]\\d{2})?( ?[$€£])?$`;
      default:
        validationErrorMsg.value = `Die Eingabe muss zwischen ${min} und ${max} Zeichen lang sein.`;
        // Fallback regex if no input type matches
        return `^.{${min},${max}}$`;
    }
  });

  // Computed property for validation
  const isValid = computed(() => {
    if (inputvalue.value == null || inputvalue.value?.length === 0) return true;
    return new RegExp(regexPattern.value).test(inputvalue.value);
  });
  const errorMessage = ref('');

  // Method to validate input
  const validateInput = () => {
    if (!isValid.value && inputvalue.value.length > 0) {
      errorMessage.value = validationErrorMsg.value;
    } else {
      errorMessage.value = '';
    }

    // emit('update:modelValue', inputvalue.value);
    if (inputvalue.value !== props.modelValue) {
      emit('update:modelValue', inputvalue.value);
    }
  };

  const inputChanged = () => {
    if (props.inputType === 'currency') {
      const cleaned = parseCurrency(inputvalue.value);
      if (cleaned === null) {
        return;
      }

      inputvalue.value = formatCurrency(cleaned);
      if (inputvalue.value !== props.modelValue) {
        emit('update:modelValue', inputvalue.value);
      }
    }
    // validateInput();
  };

  /**
   * Entfernt Symbole, extrahiert eine gültige Zahl
   * Gibt `null` zurück, wenn nicht gültig
   */
  function parseCurrency(input: string | null) {
    if (!input) return null;

    // Ersetze Komma durch Punkt (für parseFloat), entferne Leerzeichen, €
    const normalized = input
      .replace(/[€\s]/g, '')
      .replace(/\./g, '') // Tausendertrennzeichen entfernen
      .replace(/,/g, '.'); // Dezimalzeichen normalisieren

    const number = parseFloat(normalized);

    if (isNaN(number)) return null;
    return number;
  }

  /**
   * Formatiert eine Zahl als Währung mit 2 Dezimalstellen, Tausenderpunkten und Komma
   * Beispiel: 1234.5 → "1.234,50"
   */
  function formatCurrency(value: number): string {
    return value.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
</script>
